<template>
  <!--
    DO NOT CHANGE: weapon images are pinned by user request at 24px. The
    weapons rail is normally 200px and uses its mirrored board-range token
    only between the custom board and xl breakpoints, where three detail
    columns share the laptop viewport.

    The weapons column root is NO LONGER a `data-no-drag` region — a drag
    gesture may start on the column's background so a player card can be
    re-ordered by grabbing the weapons area. The interactive children
    (each `WeaponSlot` and the add-build control) carry their own
    `data-no-drag` so clicks/drags starting on THEM aren't mis-read as a
    card drag. The card's outer `<article>` is the `data-drag-handle`
    (see `PlayerCard.vue`).
  -->
  <div class="flex w-full shrink-0 flex-col gap-0.5 sm:w-(--w-weapons-column) board:w-(--w-weapons-column-board) xl:w-(--w-weapons-column)">
    <button
      ref="buildsChip"
      type="button"
      data-no-drag
      :data-builds-chip-id="props.player.id"
      :class="[
        'relative flex w-full flex-col gap-0.5 rounded-sm text-left transition-[box-shadow,border-color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        buildsPopupOpen ? 'ring-1 ring-accent' : '',
      ]"
      :aria-controls="popupId"
      :aria-expanded="buildsPopupOpen"
      aria-haspopup="dialog"
    >
      <span
        v-for="slot in mainSlots"
        :key="`m-${slot.key}`"
        class="flex min-h-8 w-full items-center gap-2 overflow-hidden rounded-sm border border-l-[3px] border-border bg-bg-surface p-1"
        :style="slot.weapon ? { borderLeftColor: roleColors.byColor[slot.weapon.color].borderVar } : undefined"
      >
        <img
          v-if="slot.weapon"
          :src="slot.weapon.icon"
          :alt="localizedWeaponName(slot.weapon)"
          class="w-6 h-6 shrink-0 object-cover rounded-sm block"
          draggable="false"
        >
        <span class="text-sm font-medium text-text-primary leading-[1.1] overflow-hidden text-ellipsis whitespace-nowrap">{{ slot.weapon ? localizedWeaponName(slot.weapon) : messages.slot.chooseBuild }}</span>
      </span>
    </button>

    <PlayerBuildsPopup
      :player="props.player"
      :open="buildsPopupOpen"
      :trigger-selector="triggerSelector"
      :active-build-index="props.player.activeBuildIndex"
      :can-select-builds="props.canSelectBuilds"
      :can-create-build="canAddBuild"
      :can-set-default-build="props.canSetDefaultBuild"
      :can-select-guild-builds="props.canSelectGuildBuilds"
      :can-use-unassigned-guild-builds="props.canUseUnassignedGuildBuilds"
      :can-assign-guild-builds="props.canAssignGuildBuilds"
      :can-manage-guild-builds="props.canManageGuildBuilds"
      :can-create-guild-build="canAddGuildBuild"
      @update:open="onBuildsPopupUpdate"
      @use-build="(id, bi) => emit('use-build', id, bi)"
      @add-build="emit('add-build', props.player.id)"
      @use-guild-build="(id, buildId) => emit('use-guild-build', id, buildId)"
      @add-guild-build="emit('add-guild-build')"
      @edit-guild-build="(buildId, slot) => emit('edit-guild-build', props.player.id, buildId, slot)"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * PlayerWeaponSlots — the weapons column on a `PlayerCard`. Owns the
 * main weapon cells and the build-selector popup state machine;
 * the popup markup itself lives in `PlayerBuildsPopup.vue` so the host
 * card stays under the file-size cap.
 *
 * Every main-weapon row opens the saved-build selector. The popup decides
 * whether its rows are actionable from the parent-provided live/raid
 * permission gate. Direct main-weapon mutation is intentionally absent:
 * selecting or creating a complete saved build is the only card-level flow.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { WEAPONS_BY_SLUG } from '@/data/weapons'
import { useRoleColors } from '@/features/gvg-planner/composables'
import { weaponDisplayName } from '@/features/gvg-planner/lib/roleLabels'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import type { Weapon } from '@/types/weapon'
import { WeaponRole } from '@/types/weapon'
import { WEAPON_ROLE_ORDER } from '../../lib/roleSort'
import { MAX_SAVED_BUILDS } from '../../lib/boardInvariants'
import { playerCurrentWeaponSlugs } from '../../lib/playerWeaponBuilds'
import type { Player } from '../../types'
import type { ViewMode } from '../../types'
import PlayerBuildsPopup from './PlayerBuildsPopup.vue'
import { weaponsEn } from './locales/en.ts'
import { weaponsRu } from './locales/ru.ts'

export interface PlayerWeaponSlotsProps {
  readonly player: Player
  /** View mode forwarded for parity with the parent card. Weapon slots only render in `Detail` mode. */
  readonly mode: ViewMode
  /** True only when the current live/raid resolver will accept a build selection. */
  readonly canSelectBuilds: boolean
  /** True only for the matching Discord user on the live board. */
  readonly canManageBuilds: boolean
  /** True for the matching Discord user, who owns default-build ordering. */
  readonly canSetDefaultBuild: boolean
  /** True when the player may activate a guild build for this live board. */
  readonly canSelectGuildBuilds: boolean
  /** Raid snapshots may select a guild build without changing its live assignment. */
  readonly canUseUnassignedGuildBuilds: boolean
  /** True for officers when the card resolves to a live guild player. */
  readonly canAssignGuildBuilds: boolean
  /** True only for guild officers and owners on the live board. */
  readonly canManageGuildBuilds: boolean
}

const props = defineProps<PlayerWeaponSlotsProps>()
const catalog = defineLocaleCatalog({ en: weaponsEn, ru: weaponsRu })
const messages = useLocaleMessages(catalog)

const emit = defineEmits<{
  /** A build was selected in the popup; parent should dispatch `store.setActiveBuild`. */
  (e: 'use-build', playerId: string, buildIndex: number): void
  /** The owner requested the shared picker for a new two-weapon build. */
  (e: 'add-build', playerId: string): void
  /** A guild build row was activated for this player. */
  (e: 'use-guild-build', playerId: string, buildId: string): void
  /** The owner requested the shared picker for a new two-weapon guild build (no player scope). */
  (e: 'add-guild-build'): void
  /** The owner requested a slot-edit on an existing guild build. */
  (e: 'edit-guild-build', playerId: string, buildId: string, slot: 0 | 1): void
}>()

/** Stable id mirrored on the popup so the trigger can declare `aria-controls`. */
const popupId = computed<string>(() => `builds-popup-${props.player.id}`)
const roleColors = useRoleColors()
/**
 * CSS selector `PlayerBuildsPopup` uses to anchor its `AppDropdown`
 * to the current-build weapon cells. `AppDropdown` owns the open/close
 * state machine and only renders the floating popup content.
 */
const triggerSelector = computed<string>(() => `[data-builds-chip-id="${props.player.id}"]`)

const canAddBuild = computed<boolean>(() =>
  props.canManageBuilds && props.player.builds.length < MAX_SAVED_BUILDS,
)

const canAddGuildBuild = computed<boolean>(() =>
  props.canManageGuildBuilds,
)

/**
 * Display-time sort priority for a main weapon. Lower numbers render first.
 *
 * The store keeps `player.mainWeapons` in insertion order — we never
 * mutate it here. The card simply re-orders for display using the
 * canonical board `WEAPON_ROLE_ORDER` (TANK → DPS → HEAL) so the weapon
 * strip on every card lines up with the player-list sort and the
 * room-level weapon-stats popup. Sourcing the priority from the shared
 * constant instead of an inline switch keeps the priority in lockstep
 * with `roleSort.ts` and lets new roles land in the same place
 * everywhere with one edit.
 *
 * Exhaustive over the current `WeaponRole` union so adding a new role
 * causes a compile error here.
 */
const MAIN_WEAPON_PRIORITY: Readonly<Record<WeaponRole, number>> = {
  [WeaponRole.Tank]: WEAPON_ROLE_ORDER.indexOf(WeaponRole.Tank),
  [WeaponRole.Dps]: WEAPON_ROLE_ORDER.indexOf(WeaponRole.Dps),
  [WeaponRole.Heal]: WEAPON_ROLE_ORDER.indexOf(WeaponRole.Heal),
}

function mainWeaponSortPriority(weapon: Weapon): number {
  return MAIN_WEAPON_PRIORITY[weapon.role]
}

function slotWeapon(slug: string | undefined): Weapon | undefined {
  return slug === undefined ? undefined : WEAPONS_BY_SLUG.get(slug)
}

function localizedWeaponName(weapon: Weapon): string {
  return weaponDisplayName(weapon.slug, weapon.name, messages.value.names)
}

/** A single renderable main-weapon slot. */
interface MainSlot {
  readonly slug: string | undefined
  readonly key: string
  readonly weapon: Weapon | undefined
}

/**
 * Build the two displayed main-weapon slots in TANK → DPS → HEAL
 * order, matching the canonical `WEAPON_ROLE_ORDER` so the card strip
 * agrees with the player-list sort and the room-level weapon-stats
 * popup.
 *
 * `playerCurrentWeaponSlugs` is shared with roster filters so legacy raid rows
 * that render a saved-build fallback are searchable by those visible weapons.
 * A trailing slot is `undefined` so the layout keeps a stable "add"
 * placeholder. The `key` is the underlying store index (or `-1` for the empty
 * placeholder) so Vue can track identity across re-sorts — same weapon
 * keeps its DOM node when priorities tie.
 */
const mainSlots = computed<ReadonlyArray<MainSlot>>(() => {
  const displayWeapons = playerCurrentWeaponSlugs(props.player)
  const indexed: ReadonlyArray<{ slug: string; storeIndex: number; priority: number }> = displayWeapons
    .map((slug, storeIndex) => {
      const weapon = WEAPONS_BY_SLUG.get(slug)
      const priority = weapon ? mainWeaponSortPriority(weapon) : 1
      return { slug, storeIndex, priority }
    })
  const sorted = indexed
    .slice()
    .sort((a, b) => (a.priority - b.priority) || (a.storeIndex - b.storeIndex))
  const first = sorted[0]
  if (!first) {
    return [
      { slug: undefined, key: '-1', weapon: undefined },
      { slug: undefined, key: '-2', weapon: undefined },
    ]
  }
  const second = sorted[1]
  if (!second) {
    return [
      {
        slug: first.slug,
        key: String(first.storeIndex),
        weapon: slotWeapon(first.slug),
      },
      { slug: undefined, key: '-2', weapon: undefined },
    ]
  }
  return [
    {
      slug: first.slug,
      key: String(first.storeIndex),
      weapon: slotWeapon(first.slug),
    },
    {
      slug: second.slug,
      key: String(second.storeIndex),
      weapon: slotWeapon(second.slug),
    },
  ]
})

// ----- current-build selector / popup shared state -----
/**
 * The host owns the open flag so the current-build cells and popup share a single
 * source of truth. The popup component itself is purely presentational.
 */
const buildsChip = ref<HTMLButtonElement | null>(null)
const buildsPopupOpen = ref<boolean>(false)
// `buildsChip` stays bound to the current-build container for the dropdown anchor.
void buildsChip

/**
 * `AppDropdown` binds the current-build container through `triggerSelector` and
 * owns click, keyboard activation, and Escape behavior. Do not add a local toggle:
 * that would run alongside VaDropdown's anchor toggle and cancel it out.
 */
function onBuildsPopupUpdate(open: boolean): void {
  buildsPopupOpen.value = open
}

/**
 * The trigger and popup live in separate DOM subtrees because VaDropdown
 * teleports its content. Treat both as one interaction boundary and close on
 * pointer-down everywhere else. VaDropdown's own outside-click handler cannot
 * do this reliably because the external trigger is outside its wrapper.
 */
function onDocumentPointerDown(event: PointerEvent): void {
  if (!buildsPopupOpen.value || !(event.target instanceof Node)) return
  if (buildsChip.value?.contains(event.target)) return
  if (document.getElementById(popupId.value)?.contains(event.target)) return
  buildsPopupOpen.value = false
}

onMounted(() => document.addEventListener('pointerdown', onDocumentPointerDown, true))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointerDown, true))
</script>
